import { useContext, useEffect, useState } from 'react';
import moment, { type Moment } from 'moment';
import 'moment/locale/en-gb';
import { Box, Grid } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MetadataError } from '@ep/error-handling';
import { GroupContext } from '../../../context/GroupContext';
import { UserContext } from '../../../context/UserContext';
import ExportDataButton, {
  type ExportRequestParameters,
  type ExportParametersProps
} from '../ExportDataButton';
import { momentISOString, momentSnapDayUTC } from '../../../util/date';
import ReplicationRowSelect from '../../shared/input/select/ReplicationRowSelect';
import { site_label } from '../../../lib/metric_capture/site';
import type { ReplicationRow, ReplicationRowValue } from '@esg/esg-global-types';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import { getReplicationRows } from '../../../graphql/services/replication.service';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import BasicSelect from '../../shared/input/select/BasicSelect';
import { CompanyContext } from '../../../context/CompanyContext';

const SOURCE = 'RawDailyConsumptionDataUtilityManagementExportParameters.tsx';

// Replication entities to use for export filters.
interface RawDailyConsumptionUtilityManagementReplicationEntities {
  sites: Array<ReplicationRow>;
}

// Data sent in request to unique Cloud Function.
interface RawDailyConsumptionDataUtilityManagementExportRequestParameters extends ExportRequestParameters {
  group_id: string;
  company_id: string;
  source_client_dataset: string;
  start_date: string;
  end_date: string;
  site_ids: Array<string>;
}

/**
 * Enables users to export utility daily consumption data as csv for company.
 * @param {ExportParametersProps} export_type The structure of the export which includes metadata, api endpoint, permissions and more.
 * @returns {JSX.Element}
 */
const RawDailyConsumptionDataUtilityManagementExportParameters = ({
  export_type
}: ExportParametersProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!user || !group || !company) {
    return null;
  }

  // Default parameters state for inputs.
  const initial_parameters: RawDailyConsumptionDataUtilityManagementExportRequestParameters = {
    group_id: group.id,
    company_id: company.id,
    source_client_dataset: company.client_bigquery_dataset,
    start_date: '',
    end_date: '',
    site_ids: []
  };

  const initial_entities_map: RawDailyConsumptionUtilityManagementReplicationEntities = {
    sites: []
  };

  const [rawDailyConsumptionExportParameters, setRawDailyConsumptionExportParameters] =
    useState<RawDailyConsumptionDataUtilityManagementExportRequestParameters>(initial_parameters);
  const [replicationEntities, setReplicationEntities] =
    useState<RawDailyConsumptionUtilityManagementReplicationEntities>(initial_entities_map);
  const [selectedSites, setSelectedSites] = useState<Array<ReplicationRow>>([]);
  const [selectedCategories, setSelectedCategories] = useState<Array<string>>([]);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);

  // Validation check for unique export parameters.
  const parameters_valid = Boolean(
    startDate &&
    endDate &&
    startDate >= moment().subtract(2, 'years').subtract(1, 'day') &&
    momentSnapDayUTC(endDate) >= momentSnapDayUTC(startDate) &&
    endDate < moment().add(1, 'day') &&
    rawDailyConsumptionExportParameters.site_ids.length
  );

  const site_categories: Set<string> = new Set(
    replicationEntities.sites.reduce(
      (current_categories: Array<string>, next_site: ReplicationRow) => {
        return [
          ...current_categories,
          ...(next_site.data.category_1 && typeof next_site.data.category_1 === 'string'
            ? [next_site.data.category_1]
            : [])
        ];
      },
      []
    )
  );

  const handleStartDateChange = (start_date: Moment | null): void => {
    // Snap local selected start date to same day 00:30 at UTC for backwards integrated raw data.
    const snapped_start_date: Moment | null = start_date
      ? momentSnapDayUTC(start_date).minute(30)
      : null;
    const updated_parameters: RawDailyConsumptionDataUtilityManagementExportRequestParameters = {
      ...rawDailyConsumptionExportParameters,
      start_date: snapped_start_date ? momentISOString(snapped_start_date) : ''
    };
    setStartDate(start_date);
    setRawDailyConsumptionExportParameters(updated_parameters);
  };

  const handleEndDateChange = (end_date: Moment | null): void => {
    const snapped_end_date: Moment | null = end_date ? momentSnapDayUTC(end_date) : null;
    const updated_parameters: RawDailyConsumptionDataUtilityManagementExportRequestParameters = {
      ...rawDailyConsumptionExportParameters,
      end_date: snapped_end_date ? momentISOString(snapped_end_date) : ''
    };
    setEndDate(end_date);
    setRawDailyConsumptionExportParameters(updated_parameters);
  };

  const handleCategoriesChange = (categories: Array<string>): void => {
    const updated_parameters: RawDailyConsumptionDataUtilityManagementExportRequestParameters = {
      ...rawDailyConsumptionExportParameters,
      site_ids: []
    };
    setSelectedCategories(categories);
    setSelectedSites([]);
    setRawDailyConsumptionExportParameters(updated_parameters);
  };

  const handleSitesChange = (sites: Array<ReplicationRow>): void => {
    const updated_parameters: RawDailyConsumptionDataUtilityManagementExportRequestParameters = {
      ...rawDailyConsumptionExportParameters,
      site_ids: sites.map((site: ReplicationRow) => site.id)
    };
    setSelectedSites(sites);
    setRawDailyConsumptionExportParameters(updated_parameters);
  };

  const getFilteredSites = (): Array<ReplicationRow> => {
    return replicationEntities.sites.filter((site: ReplicationRow) => {
      const site_category: ReplicationRowValue = site.data.category_1;
      return (
        selectedCategories.length === 0 ||
        (!!site_category &&
          typeof site_category === 'string' &&
          selectedCategories.includes(site_category))
      );
    });
  };

  // Fetch replication entities needed for export parameter filtering.
  const fetchReplicationEntities = async () => {
    try {
      const [sites]: [Array<ReplicationRow>] = await Promise.all([
        getReplicationRows(
          {
            group_id: group.id,
            company_id: company.id,
            replication_id: 'bigquery_utility_management',
            table_id: 's_configuration_sites'
          },
          {}
        )
      ]);
      if (sites) {
        setReplicationEntities({ sites });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE),
          {
            group: group,
            company: company,
            user: user
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch sites', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  useEffect(() => {
    (async () => {
      setRawDailyConsumptionExportParameters(initial_parameters);
      setReplicationEntities(initial_entities_map);
      if (group && company && user) {
        await fetchReplicationEntities();
      }
    })();
  }, [user, company, group]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Grid container spacing={2}>
        <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
          <Box width={350}>
            <DatePicker<Moment>
              label={'Start Date'}
              value={startDate}
              maxDate={endDate || moment().subtract(1, 'day')}
              minDate={
                endDate
                  ? moment.min(endDate.clone().subtract(1, 'year'), moment().subtract(2, 'year'))
                  : moment().subtract(2, 'year')
              }
              onChange={(new_date) => handleStartDateChange(new_date)}
              sx={{ width: '100%' }}
            />
          </Box>
          <Box width={350}>
            <DatePicker<Moment>
              label={'End Date'}
              value={endDate}
              maxDate={
                startDate
                  ? moment.min(startDate.clone().add(1, 'year'), moment().subtract(1, 'day'))
                  : moment().subtract(1, 'day')
              }
              minDate={startDate ? startDate : moment().subtract(2, 'years')}
              onChange={(new_date) => handleEndDateChange(new_date)}
              sx={{ width: '100%' }}
            />
          </Box>
        </Grid>
        {!!rawDailyConsumptionExportParameters.start_date &&
          !!rawDailyConsumptionExportParameters.end_date && (
            <>
              <Grid size={12} sx={{ display: 'flex', gap: 2 }}>
                <Box width={350}>
                  <BasicSelect
                    input_label="Category"
                    options={[...site_categories]}
                    multi_select
                    disable_selected_tags
                    select_all
                    selected_options={selectedCategories}
                    handleChangeSelection={(categories: string | Array<string> | null): void => {
                      if (Array.isArray(categories)) {
                        handleCategoriesChange(categories);
                      }
                    }}
                  />
                </Box>
                <Box width={350}>
                  <ReplicationRowSelect
                    disable_selected_tags
                    multi_select
                    options={getFilteredSites()}
                    selected_options={selectedSites}
                    replication_entity_label={site_label}
                    option_label_field="site_name"
                    select_all
                    handleChangeReplicationRows={(
                      rows: Array<ReplicationRow> | ReplicationRow | null
                    ) => {
                      if (Array.isArray(rows)) {
                        handleSitesChange(rows);
                      }
                    }}
                  />
                </Box>
              </Grid>
            </>
          )}
        <Grid size={12} sx={{ textAlign: 'left' }}>
          <ExportDataButton
            user={user}
            export_type={export_type}
            parameters={rawDailyConsumptionExportParameters}
            disabled={!parameters_valid}
            label="Export"
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default RawDailyConsumptionDataUtilityManagementExportParameters;
